import React from "react"
import { Alert, Card, CardContent, FormControlLabel, Grid, Stack, Switch, TextField, Typography } from "@mui/material"

import { LanguageDropdownWidget } from "react-ui-components/components/widgets/LanguageDropdownWidget"
import {
  CityLocationSelector,
  CountryLocationSelector,
  LocationProvider,
  StateLocationSelector,
} from "../../LocationSelector"
import { WheelConfigFormData, WheelLocationFormData } from "./wheelPrizeTypes"

const millisToLocalInput = (millis: number | null): string => {
  if (!millis) return ""
  const date = new Date(millis)
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const localInputToMillis = (value: string): number | null => {
  if (!value) return null
  const millis = new Date(value).getTime()
  return Number.isFinite(millis) ? millis : null
}

export const WheelSetupPanel: React.FC<{
  config: WheelConfigFormData
  onChange: (patch: Partial<WheelConfigFormData>) => void
}> = ({ config, onChange }) => {
  const patchLocation = (field: "country" | "state" | "city", value: string) => {
    const location: WheelLocationFormData = { ...config.location, [field]: value }
    // Clear dependents so a stale city/state can't survive a country/state change and get
    // saved as an impossible-to-match combination (resolveWheelConfig requires an exact match).
    if (field === "country") {
      location.state = ""
      location.city = ""
    } else if (field === "state") {
      location.city = ""
    }
    onChange({ location })
  }

  return (
  <Stack spacing={3}>
    {!config.isEnabled && <Alert severity="warning">The wheel is disabled — hidden from users.</Alert>}

    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Name, location & language
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create one wheel per location/language combination you need (e.g. "US - English",
          "US - Spanish"). A wheel with no location or language set applies wherever nothing more
          specific matches — mark exactly one wheel as the default.
        </Typography>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Wheel name"
              value={config.name}
              onChange={(event) => onChange({ name: event.target.value })}
              helperText="Admin-only label, not shown to users."
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <LanguageDropdownWidget
              label="Language"
              value={config.language}
              onChange={(value: string) => onChange({ language: value })}
              includeAllLanguagesOption
              allLanguagesLabel="All languages"
            />
          </Grid>
        </Grid>
        <LocationProvider formData={config.location}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <CountryLocationSelector value={config.location.country} onChange={patchLocation} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StateLocationSelector value={config.location.state} onChange={patchLocation} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <CityLocationSelector value={config.location.city} onChange={patchLocation} />
            </Grid>
          </Grid>
        </LocationProvider>
        <FormControlLabel
          control={
            <Switch checked={config.isDefault} onChange={(event) => onChange({ isDefault: event.target.checked })} />
          }
          label="Default wheel (fallback when no other wheel matches a user's location/language)"
        />
      </CardContent>
    </Card>

    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Availability & winner rules
        </Typography>
        <FormControlLabel
          control={<Switch checked={config.isEnabled} onChange={(event) => onChange({ isEnabled: event.target.checked })} />}
          label="Enabled (visible to users)"
        />
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Spins per user per day"
              type="number"
              value={config.dailySpinLimit}
              onChange={(event) => onChange({ dailySpinLimit: Math.max(1, Math.floor(Number(event.target.value)) || 1) })}
              inputProps={{ min: 1 }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Cooldown between spins (minutes)"
              type="number"
              value={config.cooldownMinutes}
              onChange={(event) => onChange({ cooldownMinutes: Math.max(0, Math.floor(Number(event.target.value)) || 0) })}
              inputProps={{ min: 0 }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Daily grand-prize limit"
              type="number"
              value={config.dailyGrandPrizeLimit ?? ""}
              onChange={(event) => {
                const value = event.target.value
                onChange({ dailyGrandPrizeLimit: value.trim() === "" ? null : Math.max(1, Math.floor(Number(value)) || 1) })
              }}
              helperText="Blank = unlimited"
              inputProps={{ min: 1 }}
              fullWidth
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>

    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Campaign schedule
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Campaign starts"
              type="datetime-local"
              value={millisToLocalInput(config.campaignStartAtMillis)}
              onChange={(event) => onChange({ campaignStartAtMillis: localInputToMillis(event.target.value) })}
              InputLabelProps={{ shrink: true }}
              helperText="Blank = starts immediately"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Campaign ends"
              type="datetime-local"
              value={millisToLocalInput(config.campaignEndAtMillis)}
              onChange={(event) => onChange({ campaignEndAtMillis: localInputToMillis(event.target.value) })}
              InputLabelProps={{ shrink: true }}
              helperText="Blank = no end date"
              fullWidth
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  </Stack>
  )
}

export default WheelSetupPanel
